import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Play, Globe, Smartphone, Zap } from "lucide-react";

/**
 * Portfolio catalog. Placeholder projects — swap in real client work with
 * screenshots + video URLs as they land. Every entry supports optional
 * `video`, `image`, and `href` fields.
 */
const projects = [
  // ============ AI VIDEO ============
  {
    id: "dw-investment-group-ad",
    kind: "video",
    kindLabel: "AI Video",
    title: "DW Investment Group — Signature Ad",
    client: "DW Investment Group",
    year: "2026",
    summary: "Cinematic 60-second brand ad blending real footage with AI-augmented b-roll, motion graphics, and a scripted VO track. Designed for LinkedIn, YouTube pre-roll, and investor deck loops.",
    tags: ["Veo 3", "Cinematic", "16:9", "Investor Brand"],
    stat: { k: "4.1x", v: "meeting-book uplift · Q2" },
    video: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/eedhr5il_mp4_gwr_video_mvp.mp4",
    href: "https://dwinvestmentgroup.com",
    external: true,
  },
  {
    id: "avatar-founder-vsl",
    kind: "video",
    kindLabel: "AI Video",
    title: "Founder Digital Twin — 3min VSL",
    client: "Med-Spa Network · West Coast",
    year: "2026",
    summary: "4K custom avatar cloned from CEO. Multilingual VSL (EN + ES) with lip-sync. Powers homepage + paid funnel.",
    tags: ["Digital Twin", "Multilingual", "VSL"],
    stat: { k: "3.4x", v: "landing conversion lift" },
    video: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/eedhr5il_mp4_gwr_video_mvp.mp4",
  },
  {
    id: "omni-ecom-campaign",
    kind: "video",
    kindLabel: "AI Video",
    title: "Omnichannel Ad Matrix",
    client: "Series B E-Commerce Brand",
    year: "2026",
    summary: "8 UGC-style creator variants × 6 hook A/B tests. 20+ paid-media creatives delivered in 3 weeks.",
    tags: ["UGC AI", "A/B Matrix", "Meta + TikTok"],
    stat: { k: "62%", v: "CPA reduction vs. control" },
    video: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/eedhr5il_mp4_gwr_video_mvp.mp4",
  },
  // ============ LIVE CLIENT WEBSITES ============
  {
    id: "414llc",
    kind: "website",
    kindLabel: "Website",
    title: "414 LLC — Corporate Site",
    client: "414 LLC",
    year: "2026",
    summary: "Corporate authority site engineered for a diversified holdings company. Portfolio companies, investor summary, and lead-capture routing built into a fast headless stack.",
    tags: ["Next.js", "Corporate", "Lead Routing"],
    stat: { k: "0.9s", v: "LCP · Lighthouse 96" },
    image: "https://image.thum.io/get/width/1200/crop/900/https://414llc.com",
    href: "https://414llc.com",
    external: true,
  },
  {
    id: "hillzboyz",
    kind: "website",
    kindLabel: "Website",
    title: "Hillboyz Memphis — Brand Site",
    client: "Hillboyz Memphis",
    year: "2026",
    summary: "Custom brand experience site with kinetic scroll, motion graphics, and integrated commerce hooks for a lifestyle apparel and media label.",
    tags: ["Brand", "Framer Motion", "Commerce"],
    stat: { k: "98", v: "Lighthouse mobile" },
    image: "https://image.thum.io/get/width/1200/crop/900/https://hillboyzmemphis.com",
    href: "https://hillboyzmemphis.com",
    external: true,
  },
  {
    id: "dwinvestmentgroup",
    kind: "website",
    kindLabel: "Website",
    title: "DW Investment Group — Corporate + Investor Portal",
    client: "DW Investment Group",
    year: "2026",
    summary: "Flagship investor-facing site plus a private opportunity portal. Deal listings, subscription docs, KYC intake, and a compliance-ready CRM hook.",
    tags: ["Investor Portal", "Compliance", "Next.js"],
    stat: { k: "$18M+", v: "capital pipeline routed" },
    image: "https://image.thum.io/get/width/1200/crop/900/https://dwinvestmentgroup.com",
    href: "https://dwinvestmentgroup.com",
    external: true,
  },
  {
    id: "whipitup",
    kind: "website",
    kindLabel: "Website",
    title: "Whip It Up — E-Commerce",
    client: "Whip It Up",
    year: "2026",
    summary: "Headless e-commerce shop with animated PDPs, subscription bundles, and a mobile-first checkout tuned for paid-social traffic conversion.",
    tags: ["Shopify Plus", "Subscriptions", "PDP"],
    stat: { k: "3.1%", v: "checkout conversion rate" },
    image: "https://image.thum.io/get/width/1200/crop/900/https://whipitup.shop",
    href: "https://whipitup.shop",
    external: true,
  },
  {
    id: "starsofautism",
    kind: "website",
    kindLabel: "Website",
    title: "Stars of Autism — Nonprofit",
    client: "Stars of Autism",
    year: "2026",
    summary: "Mission-driven nonprofit site with donation flow, event registration, resource library, and AA-level accessibility compliance across every page.",
    tags: ["Nonprofit", "Accessibility AA", "Donations"],
    stat: { k: "AA", v: "WCAG 2.1 compliant" },
    image: "https://image.thum.io/get/width/1200/crop/900/https://starsofautism.org",
    href: "https://starsofautism.org",
    external: true,
  },
  {
    id: "makebadesigns",
    kind: "website",
    kindLabel: "Website",
    title: "MakeBa Designs — Studio Portfolio",
    client: "MakeBa Designs",
    year: "2026",
    summary: "High-end design studio portfolio with cinematic hero, project case studies, and an inquiry funnel routed straight into the studio's project inbox.",
    tags: ["Portfolio", "CMS", "Cinematic"],
    stat: { k: "2.4x", v: "inquiry lift · Q1" },
    image: "https://image.thum.io/get/width/1200/crop/900/https://makebadesigns.com",
    href: "https://makebadesigns.com",
    external: true,
  },
  {
    id: "dwmortgagehub",
    kind: "website",
    kindLabel: "Website",
    title: "DW Mortgage Hub — Loan Ops",
    client: "DW Mortgage Hub",
    year: "2026",
    summary: "Consumer-facing mortgage origination site with rate calculators, loan-officer routing, borrower portal, and encrypted document intake.",
    tags: ["FinServ", "Portal", "Encrypted Intake"],
    stat: { k: "42%", v: "borrower app completion" },
    image: "https://image.thum.io/get/width/1200/crop/900/https://dwmortgagehub.com",
    href: "https://dwmortgagehub.com",
    external: true,
  },
  {
    id: "inix",
    kind: "website",
    kindLabel: "Website",
    title: "INIX — Tech Consultancy",
    client: "INIX",
    year: "2026",
    summary: "Technology consultancy authority site — service capabilities, case-study grid, insights blog, and a scoping-call funnel built to route enterprise inbounds.",
    tags: ["Consultancy", "Insights", "Enterprise"],
    stat: { k: "5.6x", v: "avg. deal size uplift" },
    image: "https://image.thum.io/get/width/1200/crop/900/https://inix.it.com",
    href: "https://inix.it.com",
    external: true,
  },
  // ============ APPS / SaaS ============
  {
    id: "dw-ecosystem",
    kind: "app",
    kindLabel: "SaaS App",
    title: "DW Ecosystem",
    client: "DW Investment Group",
    year: "2026",
    summary: "Fully integrated, AI-driven platform handling the entire real estate, mortgage, education, and digital closing lifecycle under one roof. Unified operator command center.",
    tags: ["Real Estate", "Mortgage", "Digital Closing", "AI Ops"],
    stat: { k: "1", v: "platform · 4 verticals" },
    href: "https://dwinvestmentgroup.com",
    external: true,
  },
  {
    id: "client-portal",
    kind: "app",
    kindLabel: "SaaS App",
    title: "TraceElements Client Portal",
    client: "TraceElements Labs",
    year: "2026",
    summary: "Production client dashboard: project tracker, AI-generated updates, Stripe billing, drag-drop asset vault, and 24-hour AI concierge.",
    tags: ["React", "FastAPI", "Emergent LLM", "Object Storage"],
    stat: { k: "0", v: "middlemen · founder-direct" },
    href: "/portal",
    external: false,
  },
  {
    id: "os-platform",
    kind: "app",
    kindLabel: "SaaS App",
    title: "TraceElements OS",
    client: "Managed CRM Platform",
    year: "2026",
    summary: "Replaces multi-tool software stacks (Calendly, Mailchimp, ActiveCampaign, Podia) with a single centralized platform that drives customer acquisition, automated follow-ups, and long-term retention.",
    tags: ["Whitelabel", "AI Voice", "Multi-tenant"],
    stat: { k: "3", v: "tiers · $297–$997/mo" },
    href: "/services/os",
    external: false,
  },
  {
    id: "saas-mvp-build",
    kind: "app",
    kindLabel: "SaaS App",
    title: "Pre-Seed SaaS MVP",
    client: "Confidential · Stealth Startup",
    year: "2025",
    summary: "Full production MVP: auth, Stripe subscription billing, Mixpanel PLG, Product Hunt launch — shipped in 11 weeks.",
    tags: ["Next.js", "FastAPI", "Stripe", "PLG"],
    stat: { k: "11 wk", v: "concept → paying users" },
  },
];

const filters = [
  { id: "all", label: "All Work", Icon: Zap },
  { id: "video", label: "AI Video", Icon: Play },
  { id: "website", label: "Websites", Icon: Globe },
  { id: "app", label: "SaaS Apps", Icon: Smartphone },
];

export default function Work() {
  const [tab, setTab] = useState("all");
  const filtered = useMemo(
    () => (tab === "all" ? projects : projects.filter((p) => p.kind === tab)),
    [tab]
  );

  return (
    <div className="bg-[#0B0C10] text-[#F5F5F7]" data-testid="work-page">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-[#1F2833]">
        <div className="absolute inset-0 te-grid-bg opacity-40" aria-hidden />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2, ease: "easeOut" }}
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-emerald-400/5 blur-[140px]"
          aria-hidden
        />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-6 flex items-center gap-3"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 te-pulse-dot" />
            // WORK.CATALOG · shipped
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-4xl sm:text-6xl lg:text-8xl font-bold tracking-tighter leading-[0.95] max-w-5xl"
          >
            The work<span className="text-emerald-400">,</span> <br className="hidden sm:block" />
            not the pitch<span className="text-[#FF3355]">.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-8 text-base sm:text-lg text-[#C5C6C7] max-w-2xl leading-relaxed"
          >
            A running catalog of what we&apos;ve shipped — AI video vaults, marketing sites,
            production SaaS apps. Some clients confidential, most metrics real, every build
            founder-led.
          </motion.p>
        </div>
      </section>

      {/* ============ FILTER + GRID ============ */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-10">
          {filters.map((f) => {
            const isActive = tab === f.id;
            const count = f.id === "all" ? projects.length : projects.filter((p) => p.kind === f.id).length;
            return (
              <button
                key={f.id}
                onClick={() => setTab(f.id)}
                data-testid={`work-filter-${f.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] border transition-all ${
                  isActive
                    ? "border-emerald-400 bg-emerald-400/10 text-emerald-400"
                    : "border-[#1F2833] text-[#C5C6C7] hover:border-[#B91C2C] hover:text-[#F5F5F7]"
                }`}
              >
                <f.Icon size={12} />
                {f.label}
                <span className="opacity-60">· {count}</span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`work-card-${p.id}`}
              className="group relative bg-[#0d1117] border border-[#1F2833] overflow-hidden flex flex-col hover:border-[#FF3355]/60 transition-all"
            >
              {/* Preview surface */}
              <div className="relative aspect-[16/10] bg-[#0B0C10] overflow-hidden border-b border-[#1F2833]">
                {p.video ? (
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                  >
                    <source src={p.video} type="video/mp4" />
                  </video>
                ) : p.image ? (
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-500"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="font-display text-6xl font-bold text-[#1F2833] group-hover:text-[#FF3355]/40 transition-colors">
                      {p.kind === "website" ? "</>" : p.kind === "app" ? "{ }" : "AI"}
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1117] via-transparent to-transparent pointer-events-none" />
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#0B0C10]/85 backdrop-blur-sm border border-emerald-400/40 text-emerald-400 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.22em] font-bold">
                  {p.kindLabel}
                </div>
                <div className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-[0.22em] text-[#C5C6C7]/70 bg-[#0B0C10]/85 backdrop-blur-sm px-2 py-0.5 border border-[#1F2833]">
                  {p.year}
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-1">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C]">
                  {p.client}
                </div>
                <h3 className="mt-2 font-display text-xl font-bold tracking-tight text-[#F5F5F7] group-hover:text-[#FF3355] transition-colors">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-[#C5C6C7] leading-relaxed line-clamp-3 flex-1">
                  {p.summary}
                </p>

                {p.tags?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {p.tags.map((t) => (
                      <span
                        key={t}
                        className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#C5C6C7] border border-[#1F2833] px-2 py-0.5"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-5 pt-4 border-t border-[#1F2833] flex items-end justify-between gap-3">
                  {p.stat && (
                    <div className="min-w-0">
                      <div className="font-display text-2xl font-bold text-emerald-400 leading-none">
                        {p.stat.k}
                      </div>
                      <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">
                        {p.stat.v}
                      </div>
                    </div>
                  )}
                  {p.href && (
                    p.external ? (
                      <a
                        href={p.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355] hover:text-[#F5F5F7] transition-colors shrink-0"
                      >
                        Open <ArrowUpRight size={12} />
                      </a>
                    ) : (
                      <Link
                        to={p.href}
                        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355] hover:text-[#F5F5F7] transition-colors shrink-0"
                      >
                        View <ArrowUpRight size={12} />
                      </Link>
                    )
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="py-16 border-t border-[#1F2833]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 border border-[#1F2833] bg-[#0d1117] p-8 lg:p-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-3">
              // YOUR.PROJECT.NEXT
            </div>
            <h3 className="font-display text-2xl lg:text-4xl font-bold tracking-tight max-w-2xl">
              Ready to be in this catalog?
            </h3>
            <p className="mt-3 text-[#C5C6C7] max-w-xl">
              Every build here started with a 30-minute call. No decks. No sales-team gauntlet.
            </p>
          </div>
          <Link
            to="/terminal"
            data-testid="work-cta-start"
            className="inline-flex items-center gap-2 bg-emerald-400 text-[#0B0C10] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-emerald-300 transition-colors te-emerald-glow-sm"
          >
            Start your build <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
